import { useState, type MouseEvent } from "react";
import { EllipsisVertical, Eye, Pencil, Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { casePath, caseWorkspacePath } from "../features/caseIde/routes";
import AddWorkflowIcon from "../assets/svgs/AddWorkflow.svg?react";
import { IndividualsAPI } from "../api/individuals";
import { deleteCase } from "../redux/clients/clientsSlice";
import { AddWorkflowModal } from "./common/AddWorkflowModal";
import { CustomAlertDialog } from "./common/CustomAlertDialog";
import { useAppTabsStore } from "../features/appTabs/context/appTabsStore";
import { caseTabId } from "../features/appTabs/tabRoutes";

/** Menu `onSelect` passes a native event; the alert dialog passes a React one. */
type StoppableEvent = Pick<Event, "stopPropagation">;

type Props = {
  caseId: string;
  /** Client the case is linked to, when known; keeps links client-scoped. */
  foreignNationalId?: string | null;
  clientName?: string;
  individualData?: {
    id: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    email: string;
    visa: string;
    employerInfo?: {
      legalBusinessName?: string;
    };
    employerPetitioner?: string;
    petitionerName?: string;
    title?: string;
    pronouns?: string;
  };
};

export const CaseActions = ({
  caseId,
  foreignNationalId,
  clientName,
  individualData
}: Props) => {
  const closeTab = useAppTabsStore((s) => s.close);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();
  const isWorkflowFeatureEnabled =
    import.meta.env.REACT_APP_ENABLE_CASE_DUPLICATION === "true";
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  const onWorkflowModalOpen = () => setIsWorkflowModalOpen(true);
  const onWorkflowModalClose = () => setIsWorkflowModalOpen(false);
  const navigate = useNavigate();
  const handleDelete = async (e: StoppableEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setIsDeleting(true);
      await IndividualsAPI.updateIndividual(caseId, { isDeleted: true });
      setIsDeleting(false);
      dispatch(deleteCase(caseId));
      closeTab(caseTabId(caseId));

      onClose();
    } else {
      onOpen();
    }
  };
  const handleEdit = (e: StoppableEvent) => {
    e.stopPropagation();
    navigate(casePath({ individualId: caseId, foreignNationalId }, "edit"));
  };
  const handleNavigate = (e: StoppableEvent) => {
    e.stopPropagation();
    navigate(caseWorkspacePath({ individualId: caseId, foreignNationalId }));
  };

  const handleAddWorkflow = (e: StoppableEvent) => {
    e.stopPropagation();
    onWorkflowModalOpen();
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Case actions"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-auto min-w-48"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem onSelect={handleNavigate}>
            <Eye />
            Go to Case
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleEdit}>
            <Pencil />
            Edit Case Profile
          </DropdownMenuItem>
          {isWorkflowFeatureEnabled && (
            <DropdownMenuItem onSelect={handleAddWorkflow}>
              <AddWorkflowIcon className="size-4" />
              Add New Workflow
            </DropdownMenuItem>
          )}
          <DropdownMenuItem variant="destructive" onSelect={handleDelete}>
            <Trash2 />
            Delete Case
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CustomAlertDialog
        withButton={false}
        alertTitle="Warning!"
        alertType="delete"
        close={onClose}
        confirm={handleDelete}
        isOpen={isOpen}
        isLoading={isDeleting}
      >
        <p>
          You are about to permanently delete this case and all data associated
          with it. This cannot be undone.
        </p>
        <p>Are you sure you want to continue? </p>
      </CustomAlertDialog>

      <AddWorkflowModal
        isOpen={isWorkflowModalOpen}
        onClose={onWorkflowModalClose}
        clientName={clientName}
        individualData={individualData}
      />
    </>
  );
};
